import React, { useState, useRef, useEffect } from 'react';
import { Button, ColorPicker, Switch, Select } from 'antd';
import { CheckOutlined, CloseOutlined, LinkOutlined } from '@ant-design/icons';
import axiosInstance from '../apis/axiosInstance';
import { useAdminControl } from "../store/Store"
import RelatedProductsSection from './Rel';
import RelatedProduct from './RelatedProduct';

const ProductUploadForm = ({ showMessage }) => {
  // Initial Variant Factory
  const createEmptyVariant = () => ({
    id: Math.random().toString(36).substr(2, 9),
    name: '',
    colorValue: '#1677ff',
    priceInc: '',
    inStock: true,
    visible: true
  });

  // State
  const [formData, setFormData] = useState({
    productName: '',
    categoryId: null,
    description: '',
    tagTypeId: null,
    tagValue: '',
    basePrice: '',
    offerPrice: '',
    rating: '',
    ratingCount: '',
    features: [],
    image: null,
    imagePreview: null,
    hasVariants: false,
    variantType: 'Color',
    variants: [createEmptyVariant()],
    galleryImages: [],
    relatedProduct: []
  });


  const [featureInput, setFeatureInput] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedGalleryIndex, setDraggedGalleryIndex] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [allCategories, setAllCategories] = useState({ data: {}, loading: false });
  const [tagTypesState, setTagTypesState] = useState({ data: {}, loading: false });

  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const { openDrawer, reRender } = useAdminControl();


  useEffect(() => {
    const fetchCategories = async () => {
      setAllCategories({ data: {}, loading: true });

      try {
        const response = await axiosInstance.post('/api/get_all_categories_simple/');
        setAllCategories({
          data: response.data.data,
          loading: false
        });

      } catch (error) {
        console.error('Error fetching categories:', error);
        setAllCategories({
          data: {},
          loading: false
        });
      }
    };

    fetchCategories();
  }, []);


  useEffect(() => {
    const fetchTagTypes = async () => {
      setTagTypesState({ data: {}, loading: true });

      try {
        const response = await axiosInstance.post('/api/get_all_tag_types/');
        setTagTypesState({
          data: response.data.data,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching tag types:', error);
        setTagTypesState({
          data: {},
          loading: false
        });
      }
    };

    fetchTagTypes();
  }, []);


  // Helper functions
  const safeValue = (value, defaultValue = '') => {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }
    return value.toString();
  };

  const getCurrentTagTypeDisplay = () => {
    if (!formData.tagTypeId || !tagTypesState.data[formData.tagTypeId]) {
      return '';
    }
    return tagTypesState.data[formData.tagTypeId].tag || '';
  };

  // Validate form function
  const validateForm = () => {
    const errors = {};

    if (!formData.productName.trim()) {
      errors.productName = true;
    }

    if (!formData.description.trim()) {
      errors.description = true;
    }

    if (!formData.categoryId) {
      errors.categoryId = true;
    }

    if (formData.tagTypeId && !formData.tagValue.trim()) {
      errors.tagValue = true;
    }

    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
      errors.basePrice = true;
    }

    if (!formData.image) {
      errors.image = true;
    }

    if (formData.features.length === 0) {
      errors.features = true;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Clear validation errors when user starts typing
  useEffect(() => {
    if (isSubmitted) {
      validateForm();
    }
  }, [formData.productName, formData.categoryId, formData.basePrice, formData.image, formData.features]);


  useEffect(() => {
    if (formData.tagTypeId && tagTypesState.data[formData.tagTypeId]) {
      const tagData = tagTypesState.data[formData.tagTypeId];
      if (tagData.text && tagData.text.trim() !== '') {
        setFormData(prev => ({
          ...prev,
          tagValue: tagData.text
        }));

        // Clear validation error if text is auto-filled
        if (validationErrors.tagValue) {
          setValidationErrors(prev => ({ ...prev, tagValue: false }));
        }
      } else {
        // If text is empty, clear the tagValue field
        setFormData(prev => ({
          ...prev,
          tagValue: ''
        }));
      }
    }
  }, [formData.tagTypeId, tagTypesState.data]);


  // Add this useEffect to clean up object URLs
  useEffect(() => {
    return () => {
      // Clean up gallery image preview URLs
      formData.galleryImages.forEach(img => {
        if (img.previewUrl && img.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(img.previewUrl);
        }
      });
    };
  }, [formData.galleryImages]);

  // Helper: Crop image to 1:1 Square
  const cropToSquare = (file, maxDimension = 2000) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // Calculate dimensions
        const minSize = Math.min(img.width, img.height);
        const size = Math.min(minSize, maxDimension);

        // Set canvas dimensions
        canvas.width = size;
        canvas.height = size;

        // Calculate source cropping coordinates (center crop)
        const sx = (img.width - minSize) / 2;
        const sy = (img.height - minSize) / 2;

        // Draw and resize image
        ctx.drawImage(img, sx, sy, minSize, minSize, 0, 0, size, size);

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg', // Always convert to JPEG for consistency
          0.85 // Quality: 85%
        );
      };

      img.onerror = () => reject(new Error('Image loading failed'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Form handlers
  const handleReset = () => {
    setFormData({
      productName: '',
      categoryId: null,
      description: '',
      tagTypeId: null,
      tagValue: '',
      basePrice: '',
      offerPrice: '',
      rating: '',
      ratingCount: '',
      features: [],
      image: null,
      imagePreview: null,
      hasVariants: false,
      variantType: 'Color',
      variants: [createEmptyVariant()],
      galleryImages: []
    });
    setValidationErrors({});
    setIsSubmitted(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = async (file) => {
    if (file.type.startsWith('image/')) {
      try {
        // Crop image to max 2000x2000
        const croppedBlob = await cropToSquare(file, 2000);

        // Create a new File object from the cropped blob
        const croppedFile = new File([croppedBlob], file.name, {
          type: file.type,
          lastModified: Date.now()
        });

        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            image: croppedFile, // Store the CROPPED file
            imagePreview: reader.result
          }));

          if (validationErrors.image) {
            setValidationErrors(prev => ({ ...prev, image: false }));
          }
        };
        reader.readAsDataURL(croppedBlob);

      } catch (error) {
        console.error('Error processing image:', error);
        // Fallback to original file
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            image: file,
            imagePreview: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  // Features handlers
  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');

      if (validationErrors.features) {
        setValidationErrors(prev => ({ ...prev, features: false }));
      }
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleFeatureKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFeature();
    }
  };

  // Variant Handlers
  const toggleHasVariants = () => {
    setFormData(prev => ({
      ...prev,
      hasVariants: !prev.hasVariants
    }));
  };

  const setVariantType = (type) => {
    setFormData(prev => ({
      ...prev,
      variantType: type,
      variants: [createEmptyVariant()]
    }));
  };

  const handleVariantChange = (id, field, value) => {
    setFormData(prev => {
      let newVariants = prev.variants.map(v =>
        v.id === id ? { ...v, [field]: value } : v
      );

      if (field === 'name') {
        const index = newVariants.findIndex(v => v.id === id);
        const isLastRow = index === newVariants.length - 1;
        const nameValue = String(value).trim();

        if (isLastRow && nameValue !== '') {
          newVariants.push(createEmptyVariant());
        }

        if (!isLastRow && nameValue === '') {
          const nextVariant = newVariants[index + 1];
          if (nextVariant && nextVariant.name.trim() === '') {
            newVariants.splice(index + 1, 1);
          }
        }
      }

      return { ...prev, variants: newVariants };
    });
  };

  const handleDeleteVariant = (index) => {
    setFormData(prev => {
      const newVariants = [...prev.variants];
      newVariants.splice(index, 1);

      if (newVariants.length === 0) {
        newVariants.push(createEmptyVariant());
      } else {
        const lastVariant = newVariants[newVariants.length - 1];
        if (lastVariant.name.trim() !== '') {
          newVariants.push(createEmptyVariant());
        }
      }

      return { ...prev, variants: newVariants };
    });
  };

  // Gallery Handlers
  const handleGalleryUpload = async (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newImages = [];

      await Promise.all(
        files.map(async (file) => {
          if (!file.type.startsWith('image/')) return;

          try {
            // Full-res for upload (max 2000px)
            const fullResBlob = await cropToSquare(file, 2000);
            // Thumbnail for preview (600px)
            const thumbBlob = await cropToSquare(file, 600);
            const thumbPreviewUrl = URL.createObjectURL(thumbBlob);

            // Create File object from the CROPPED blob
            const uploadFile = new File(
              [fullResBlob],
              `gallery-${Date.now()}.jpg`,
              {
                type: 'image/jpeg', // Convert all to JPEG for consistency
                lastModified: Date.now()
              }
            );

            newImages.push({
              id: Math.random().toString(36).substr(2, 9),
              file: uploadFile, // This is the cropped 2000x2000 image
              previewUrl: thumbPreviewUrl,
              assignedVariantId: null,
            });
          } catch (error) {
            console.error('Error processing gallery image:', error);
            // Fallback to original
            const thumbPreviewUrl = URL.createObjectURL(file);
            newImages.push({
              id: Math.random().toString(36).substr(2, 9),
              file: file,
              previewUrl: thumbPreviewUrl,
              assignedVariantId: null,
            });
          }
        })
      );

      setFormData((prev) => ({
        ...prev,
        galleryImages: [...prev.galleryImages, ...newImages],
      }));
    }
  };

  const handleRemoveGalleryImage = (id) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter(img => img.id !== id)
    }));
  };

  const handleAssignGalleryVariant = (imageId, variantId) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.map(img =>
        img.id === imageId ? { ...img, assignedVariantId: variantId } : img
      )
    }));
  };

  const handleGalleryDragStart = (index) => {
    setDraggedGalleryIndex(index);
  };

  const handleGalleryDragOver = (e, index) => {
    e.preventDefault();
    if (draggedGalleryIndex === null || draggedGalleryIndex === index) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = x > rect.width / 2 ? 'right' : 'left';

    setDropTarget({ index, position });
  };

  const handleGalleryDrop = (e) => {
    e.preventDefault();

    if (draggedGalleryIndex !== null && dropTarget) {
      const newGallery = [...formData.galleryImages];
      const draggedItem = newGallery[draggedGalleryIndex];

      newGallery.splice(draggedGalleryIndex, 1);

      let insertionIndex = dropTarget.index;
      if (dropTarget.position === 'right') {
        insertionIndex += 1;
      }

      if (draggedGalleryIndex < insertionIndex) {
        insertionIndex -= 1;
      }

      newGallery.splice(insertionIndex, 0, draggedItem);

      setFormData(prev => ({ ...prev, galleryImages: newGallery }));
    }

    setDraggedGalleryIndex(null);
    setDropTarget(null);
  };

  const getCleanFormData = () => {
    return {
      ...formData,
      variants: formData.variants.filter(variant => variant.name.trim() !== '')
    };
  };

  // Save handler with axios
  const handleSave = async () => {
    setIsSubmitted(true);
    const isValid = validateForm();

    if (!isValid) {
      const firstError = Object.keys(validationErrors)[0];
      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    const cleanData = getCleanFormData();
    const formDataToSend = new FormData();

    // Match backend field names exactly
    formDataToSend.append('productName', cleanData.productName);
    formDataToSend.append('categoryId', cleanData.categoryId);
    formDataToSend.append('description', cleanData.description);
    formDataToSend.append('tagTypeId', safeValue(cleanData.tagTypeId));
    formDataToSend.append('tagValue', cleanData.tagValue);
    formDataToSend.append('basePrice', safeValue(cleanData.basePrice, '0'));
    formDataToSend.append('offerPrice', safeValue(cleanData.offerPrice));
    formDataToSend.append('rating', safeValue(cleanData.rating, '0'));
    formDataToSend.append('ratingCount', safeValue(cleanData.ratingCount, '0'));
    formDataToSend.append('hasVariants', cleanData.hasVariants.toString());
    formDataToSend.append('variantType', cleanData.variantType);
    formDataToSend.append('features', JSON.stringify(cleanData.features));
    formDataToSend.append('relatedProducts', JSON.stringify(cleanData.relatedProduct));
    

    // Prepare variants with correct field names for backend
    const validVariants = cleanData.variants.map(v => ({
      id: v.id,
      name: v.name,
      colorValue: v.colorValue,
      priceInc: safeValue(v.priceInc, '0'),
      inStock: v.inStock,
      is_visible: v.visible  // Backend expects is_visible
    }));

    formDataToSend.append('variants', JSON.stringify(validVariants));

    // Main image
    if (cleanData.image) {
      formDataToSend.append('mainImage', cleanData.image);
    }

    // Gallery images
    cleanData.galleryImages.forEach((img, index) => {
      if (img.file instanceof Blob) {
        const file = img.file instanceof File ? img.file : new File([img.file], `gallery-${index}.jpg`, { type: 'image/jpeg' });
        formDataToSend.append('galleryImages', file);

        if (img.assignedVariantId && img.assignedVariantId !== '') {
          formDataToSend.append(`galleryAssignments[${index}]`, img.assignedVariantId);
        }
      }
    });

    setIsSubmitting(true);

    try {
      const response = await axiosInstance.post('/api/create_new_product/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      openDrawer(null);
      reRender();
      showMessage('success', 'Product Created Successfully!');
      handleReset();

    } catch (error) {
      let errorMessage = 'Upload failed';

      if (error.response) {
        // Server responded with error
        if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid data sent to server. Please check all fields.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message;
      }

      showMessage('error', 'Product Creation Failed!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateRelatedProducts = (relatedProducts) => {
    setFormData(prev => ({
      ...prev,
      relatedProduct: relatedProducts
    }));
  };

  return (
    <div className="w-full overflow-hidden flex flex-col">
      {/* TOP SECTION: Form Inputs & Image */}
      <div className="flex flex-col md:flex-row">
        {/* LEFT PANEL: Form Fields */}
        <div className="flex-1 p-5 md:p-6 flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              New Product
            </h2>
            <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full border border-gray-100">
              Draft
            </span>
          </div>

          {/* Row 1: Name & Category */}
          <div className="grid grid-cols-12 gap-3">
            {/* Product Name */}
            <div className="col-span-8 group relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className={`h-4 w-4 ${validationErrors.productName ? 'text-red-500' : 'text-gray-400 group-focus-within:text-brand-500'} transition-colors`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              </div>
              <input
                type="text"
                name="productName"
                placeholder="Product Name"
                value={formData.productName}
                onChange={handleChange}
                className={`w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border rounded-lg focus:ring-2 focus:border-brand-500 outline-none transition-all placeholder:text-gray-400 ${validationErrors.productName
                  ? 'border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200 focus:ring-brand-500/20'
                  }`}
              />
            </div>

            {/* Category Select */}
            <div className="col-span-4 relative group">
              <Select
                className="transparent-select"
                name="categoryId"
                value={formData.categoryId}
                onChange={(v) => {
                  handleChange({ target: { name: "categoryId", value: v } });
                  if (validationErrors.categoryId) {
                    setValidationErrors(prev => ({ ...prev, categoryId: false }));
                  }
                }}
                showSearch
                placeholder="Select category"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{
                  width: "100%",
                  paddingLeft: "10px",
                  background: "#f9fafb",
                  border: validationErrors.categoryId ? "1px solid #ef4444" : "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  minHeight: '40px'
                }}
                options={Object.entries(allCategories.data).map(([id, label]) => ({ value: id, label }))}
              />
            </div>
          </div>

          {/* Row 2: Description */}
          <div className="relative group">
            <textarea
              name="description"
              rows={2}
              placeholder="Product description..."
              value={formData.description}
              onChange={handleChange}
              className={`w-full p-3 text-sm bg-gray-50 border rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all placeholder:text-gray-400 resize-none ${validationErrors.description
                ? 'border-red-500 focus:ring-red-500/20'
                : 'border-gray-200 focus:ring-brand-500/20'
                }`}
            />
          </div>

          {/* Row 3: Prices & Ratings */}
          <div className="grid grid-cols-4 gap-3">
            {/* Base Price */}
            <div className="col-span-1 relative group">
              <input
                type="number"
                name="basePrice"
                placeholder="Base Price"
                value={formData.basePrice}
                onChange={handleChange}
                className={`w-full pl-5 pr-2 py-2 text-sm bg-gray-50 border rounded-lg focus:ring-2 focus:border-brand-500 outline-none ${validationErrors.basePrice
                  ? 'border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200 focus:ring-brand-500/20'
                  }`}
              />
            </div>

            {/* Offer Price */}
            <div className="col-span-1 relative group">
              <input
                type="number"
                name="offerPrice"
                placeholder="Offer Price"
                value={formData.offerPrice}
                onChange={handleChange}
                className="w-full pl-5 pr-2 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none text-green-700 font-medium"
              />
            </div>

            {/* Rating */}
            <div className="col-span-1 relative group">
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <svg className="h-3 w-3 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                name="rating"
                placeholder="0-5"
                value={formData.rating}
                onChange={handleChange}
                className="w-full pl-7 pr-2 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
              />
            </div>

            {/* Rating Count */}
            <div className="col-span-1 relative">
              <input
                type="number"
                name="ratingCount"
                placeholder="Count"
                value={formData.ratingCount}
                onChange={handleChange}
                className="w-full px-2 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
              />
            </div>
          </div>

          {/* Row 4: Tag System */}
          <div className={`grid grid-cols-12 gap-0 items-center bg-gray-50 rounded-lg border ${validationErrors.tagValue ? 'border-red-500' : 'border-gray-200'}`}>
            <div className="col-span-4 relative">
              <Select
                className="transparent-select"
                name="tagTypeId"
                value={formData.tagTypeId}
                onChange={(v) => {
                  handleChange({ target: { name: "tagTypeId", value: v } });
                  if (validationErrors.tagValue) {
                    setValidationErrors(prev => ({ ...prev, tagValue: false }));
                  }
                }}
                onClear={() => {
                  handleChange({ target: { name: "tagTypeId", value: null } });
                  setFormData(prev => ({ ...prev, tagValue: '' })); // Clear tagValue when tag type is cleared
                  if (validationErrors.tagValue) {
                    setValidationErrors(prev => ({ ...prev, tagValue: false }));
                  }
                }}
                showSearch
                placeholder="Select tag type"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                variant="borderless"
                allowClear
                style={{
                  width: '100%',
                  fontSize: "12px",
                  fontWeight: "450",
                  padding: '8px 12px',
                  margin: '0',
                  backgroundColor: 'transparent',
                  height: '40px'
                }}
                dropdownStyle={{
                  fontSize: "12px",
                }}
                popupMatchSelectWidth={false}
                options={Object.entries(tagTypesState.data).map(([id, tagData]) => ({
                  value: id,
                  label: tagData.tag
                }))}
              />
              <div className={`absolute right-0 top-1.5 bottom-1.5 w-px ${validationErrors.tagValue ? 'bg-red-500' : 'bg-gray-200'}`}></div>
            </div>
            <div className="col-span-8 relative">
              <svg className={`absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 ${validationErrors.tagValue ? 'text-red-500' : 'text-gray-400'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <input
                type="text"
                name="tagValue"
                placeholder={getCurrentTagTypeDisplay()
                  ? `Enter ${getCurrentTagTypeDisplay().toLowerCase()} text...`
                  : 'Select tag type first...'
                }
                value={formData.tagValue}
                onChange={(e) => {
                  handleChange(e);
                  if (validationErrors.tagValue) {
                    setValidationErrors(prev => ({ ...prev, tagValue: false }));
                  }
                }}
                disabled={!formData.tagTypeId}
                className={`w-full pl-7 pr-2 py-2.5 text-sm bg-transparent outline-none placeholder:text-gray-400 ${validationErrors.tagValue ? 'text-red-500' : ''} ${!formData.tagTypeId ? 'cursor-not-allowed opacity-50' : ''
                  }`}
              />
            </div>
          </div>

          {/* Row 5: Features */}
          <div className="flex flex-col gap-2">
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Add feature..."
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={handleFeatureKeyDown}
                className={`w-full pl-3 pr-10 py-2 text-sm bg-gray-50 border rounded-lg focus:ring-2 focus:border-brand-500 outline-none transition-all ${validationErrors.features
                  ? 'border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200 focus:ring-brand-500/20'
                  }`}
              />
              <button
                onClick={addFeature}
                className="absolute right-1.5 p-1 bg-brand-500 hover:bg-brand-600 text-white rounded-md transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.features.map((feature, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border group animate-in fade-in zoom-in duration-200 ${validationErrors.features
                    ? 'bg-red-50 text-red-700 border-red-100'
                    : 'bg-brand-50 text-brand-700 border-brand-100'
                    }`}
                >
                  {feature}
                  <button
                    onClick={() => removeFeature(idx)}
                    className="hover:text-red-500 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Image Upload */}
        <div className="w-full md:w-[240px] bg-gray-50 md:border-l border-gray-100 p-6 flex flex-col items-center border-t md:border-t-0">
          <label className="text-sm font-semibold text-gray-600 w-full text-left mb-4">Main Image</label>

          <div
            className={`
              relative w-full aspect-square max-w-[200px] rounded-xl border-2 border-dashed 
              flex flex-col items-center justify-center cursor-pointer transition-all duration-300
              ${validationErrors.image
                ? 'border-red-500 bg-red-50'
                : isDragOver
                  ? 'border-brand-500 bg-brand-50 scale-105'
                  : 'border-gray-300 bg-white hover:border-brand-400 hover:bg-gray-50'
              }
              ${formData.imagePreview ? 'border-none p-0 overflow-hidden' : ''}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {formData.imagePreview ? (
              <div className="relative w-full h-full group">
                <img
                  src={formData.imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                  <p className="text-white text-xs font-medium">Change Image</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center text-center p-4">
                <div className={`p-3 rounded-full mb-3 ${validationErrors.image ? 'bg-red-50 text-red-500' : 'bg-brand-50 text-brand-500'
                  } group-hover:scale-110 transition-transform`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-xs text-gray-500 font-medium">
                  Drag & Drop or <span className="text-brand-600 underline">Browse</span>
                </p>
                <p className="text-[10px] text-gray-400 mt-1">max 200x200px</p>
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
              accept="image/*"
            />
          </div>

          <div className="flex items-center justify-between mb-4 w-full pt-[20px] select-none">
            <span className="text-sm font-semibold text-gray-700">Product Variants</span>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.hasVariants}
                onChange={toggleHasVariants}
                size="default"
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </div>
          </div>

          {formData.hasVariants && (
            <div className="flex p-1 bg-gray-100 rounded-lg mb-3 w-full select-none">
              <button
                onClick={() => setVariantType('Color')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-xs font-medium transition-all flex-1 ${formData.variantType === 'Color'
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Color
              </button>

              <div className="w-px bg-gray-300 mx-1"></div>

              <button
                onClick={() => setVariantType('Size')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-xs font-medium transition-all flex-1 ${formData.variantType === 'Size'
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                </svg>
                Size
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MIDDLE SECTION: Variants */}
      {formData.hasVariants && (
        <div className="border-t border-gray-100 p-5 md:p-6 bg-gray-50/30 select-none">
          <div className="animate-in slide-in-from-top-2 duration-300">
            <div className="w-full overflow-x-auto rounded-lg border border-gray-100 bg-white">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/60 backdrop-blur-sm text-center">
                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-1/3">
                      Variant Name
                    </th>

                    {formData.variantType === "Color" && (
                      <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-[140px]">
                        Color
                      </th>
                    )}

                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-20">
                      Stock
                    </th>

                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-24">
                      Price (+)
                    </th>

                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-16">
                      Visible
                    </th>

                    <th className="py-3 w-10"></th>
                  </tr>
                </thead>

                <tbody className="text-sm">
                  {formData.variants.map((variant, index) => (
                    <tr
                      key={variant.id}
                      className="group border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                    >
                      {/* Name */}
                      <td className="py-2.5 px-3">
                        <input
                          type="text"
                          placeholder={
                            index === formData.variants.length - 1
                              ? "Type to add variant..."
                              : ""
                          }
                          value={variant.name}
                          onChange={(e) =>
                            handleVariantChange(variant.id, "name", e.target.value)
                          }
                          className="
                            w-full
                            bg-white
                            border border-gray-300
                            rounded-md
                            px-2.5 py-1.5
                            text-[14px]
                            placeholder:text-gray-300
                            focus:outline-none
                            focus:ring-2
                            focus:ring-blue-300
                            focus:border-blue-400
                            focus:shadow-sm
                            transition-all
                          "
                        />
                      </td>

                      {/* Color Picker */}
                      {formData.variantType === "Color" && (
                        <td className="py-2.5 text-center">
                          <ColorPicker
                            value={variant.colorValue}
                            onChange={(color) => {
                              const hex =
                                typeof color === "string"
                                  ? color
                                  : color.toHexString?.() || "#000000";
                              handleVariantChange(variant.id, "colorValue", hex);
                            }}
                            size="medium"
                            showText
                          />
                        </td>
                      )}

                      {/* Stock */}
                      <td className="py-2.5 text-center">
                        <Switch
                          checked={variant.inStock}
                          onChange={(checked) =>
                            handleVariantChange(variant.id, "inStock", checked)
                          }
                          size="medium"
                        />
                      </td>

                      {/* Price */}
                      <td className="py-2.5 px-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                            ৳
                          </span>
                          <input
                            type="number"
                            value={variant.priceInc}
                            onChange={(e) =>
                              handleVariantChange(variant.id, "priceInc", e.target.value)
                            }
                            placeholder="0.00"
                            className="
                              w-full
                              bg-white
                              border border-gray-300
                              rounded-md
                              pl-6 pr-2 py-1.5
                              text-[14px] text-right
                              focus:outline-none
                              focus:ring-2
                              focus:ring-blue-300
                              focus:border-blue-400
                              focus:shadow-sm
                              transition-all
                            "
                          />
                        </div>
                      </td>

                      {/* Visible */}
                      <td className="py-2.5 text-center">
                        <button
                          onClick={() =>
                            handleVariantChange(variant.id, "visible", !variant.visible)
                          }
                          className={`p-1.5 rounded-md transition-colors ${variant.visible
                            ? "bg-blue-50 text-blue-600 hover:bg-blue-100"
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                          {variant.visible ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 3l18 18M9.88 9.88a3 3 0 014.24 4.24M3.59 6.59A9.98 9.98 0 0112 5c4.48 0 8.27 2.94 9.54 7a10.02 10.02 0 01-4.13 5.41"
                              />
                            </svg>
                          )}
                        </button>
                      </td>

                      {/* Delete */}
                      <td className="py-2.5 text-center pr-3">
                        {index !== formData.variants.length - 1 && (
                          <button
                            onClick={() => handleDeleteVariant(index)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 011.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-2 text-[10px] text-gray-400 pl-1">
              * Fill the last row to automatically add a new variant.
            </p>
          </div>
        </div>
      )}

      {/* GALLERY SECTION */}
      <div className="border-t border-gray-100 p-5 md:p-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Product Gallery
          </h3>
          <button
            onClick={() => galleryInputRef.current?.click()}
            className="px-4 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium flex items-center gap-2 transition-all shadow-sm active:scale-95"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Images
          </button>
          <input
            type="file"
            multiple
            ref={galleryInputRef}
            onChange={handleGalleryUpload}
            className="hidden"
            accept="image/*"
          />
        </div>

        {formData.galleryImages.length === 0 ? (
          <div
            onClick={() => galleryInputRef.current?.click()}
            className="w-full h-32 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-brand-400 hover:bg-brand-50/50 hover:text-brand-500 transition-all cursor-pointer gap-2"
          >
            <svg className="w-8 h-8 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs font-medium">Click to upload multiple product images</span>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleGalleryDrop}
          >
            {formData.galleryImages.map((img, index) => (
              <div
                key={img.id}
                draggable
                onDragStart={() => handleGalleryDragStart(index)}
                onDragOver={(e) => handleGalleryDragOver(e, index)}
                className={`
                  group relative bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col
                  ${draggedGalleryIndex === index ? 'opacity-40 border-dashed border-gray-400' : 'border-gray-100'}
                `}
                style={{ height: '180px' }}
              >
                {dropTarget?.index === index && draggedGalleryIndex !== index && (
                  <div
                    className={`absolute top-0 bottom-0 w-1 bg-brand-500 z-50 shadow-[0_0_8px_rgba(14,165,233,0.8)] ${dropTarget.position === 'left' ? 'left-0' : 'right-0'
                      }`}
                  />
                )}

                <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleRemoveGalleryImage(img.id)}
                    className="p-1.5 bg-white/90 text-red-500 rounded-full hover:bg-red-50 shadow-sm backdrop-blur-sm"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 011.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <div className="absolute top-2 left-2 z-10 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="p-1.5 bg-white/90 text-gray-500 rounded-md shadow-sm backdrop-blur-sm">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                  </div>
                </div>

                <div className="flex-1 flex items-center justify-center p-3 bg-gray-50/50">
                  <img
                    src={img.previewUrl}
                    alt={`Gallery ${index}`}
                    className="w-[100px] h-[100px] object-cover rounded-lg shadow-sm"
                  />
                </div>

                <div className="p-2 border-t border-gray-100 bg-white">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center justify-center w-5 h-5 bg-gray-100 text-[10px] font-bold text-gray-500 rounded-md">
                      {index + 1}
                    </span>

                    {formData.hasVariants && formData.variantType === 'Color' ? (
                      <select
                        value={img.assignedVariantId || ''}
                        onChange={(e) => handleAssignGalleryVariant(img.id, e.target.value)}
                        className="flex-1 text-[10px] py-1 pl-1 pr-4 bg-gray-50 border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500 outline-none text-gray-600 appearance-none cursor-pointer truncate"
                        style={{
                          backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 4px center',
                          backgroundSize: '8px'
                        }}
                      >
                        <option value="">All</option>
                        {formData.variants
                          .filter(v => v.name.trim() !== '')
                          .map(v => (
                            <option key={v.id} value={v.id}>
                              {v.name}
                            </option>
                          ))
                        }
                      </select>
                    ) : (
                      <span className="text-[10px] text-gray-400 font-medium px-1">Global</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <RelatedProductsSection updateRelatedProducts={updateRelatedProducts} />

      {/* FOOTER: Actions */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
        <Button
          color="danger"
          variant="dashed"
          size='large'
          style={{ minWidth: '120px' }}
          onClick={handleReset}
          disabled={isSubmitting}
        >
          Reset
        </Button>
        <Button
          color="primary"
          variant="solid"
          size='large'
          style={{ minWidth: '120px' }}
          onClick={handleSave}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default ProductUploadForm;